// My balance (SM-310) — the one commission page every admin can open.
//
// Deliberately ungated. A CREATOR with no treasury permission at all
// still sees what they personally earned, because the scope is
// STRUCTURAL: the server pins the recipient filter to the
// authenticated actor, so there is nothing a permission could usefully
// protect here. Adding one would only lock people out of their own
// money.
//
// Where the viewer lacks poll visibility the server has already
// replaced market and event identifiers with opaque source references.
// Those are stable and distinct — enough to tell two earning sources
// apart and quote one in a support thread — without letting an admin
// enumerate a catalog they cannot otherwise see.
//
// Since SM-363 those references arrive NAMED: a balance you cannot
// attribute to anything is not a balance. The name is all that is
// added — the reference stays opaque and the row is not a link, so
// knowing what paid you is still not a way into the catalog.
//
// SM-372: "the server pins the recipient" was true of the TOTALS and
// not of the table under them. The table read the shared revenue
// report, whose scope widens with treasury permission — so an admin
// holding both opened this page and found Bootstrap admin's and Owen's
// rows listed under their own "Paid to you" figure. Every read on this
// page now goes to a /admin/me/* endpoint that pins unconditionally,
// which is what makes the two halves reconcile.
//
// SM-373: the page was named "My balance" and did not show one. Every
// figure on it was a COMMISSION total — lifetime sums that keep counting
// money after it has been withdrawn — so the number an admin came here
// for, what is in the account right now, was the one thing missing, and
// "Paid to you" was the closest thing to it. The account balance now
// leads the page, comes from the ledger rather than from arithmetic over
// the tiles, and is styled as the odd one out so the two cannot be read
// as the same claim.
import { createFileRoute } from "@tanstack/react-router";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Skeleton } from "@/components/ui/Skeleton";
import { SparkAmount } from "@/components/ui/SparkAmount";
import { EmptyState } from "@/components/ui/EmptyState";
import { LoadFailed } from "@/components/fees/LoadFailed";
import { CommissionGroupTable } from "@/components/treasury/CommissionGroupTable";
import { WithdrawPanel } from "@/components/treasury/WithdrawPanel";
import {
  commissionKeys,
  fetchMyBalance,
  fetchMyEarnings,
  payoutKeys,
  type CommissionFilters,
  type MyBalance,
} from "@/lib/queries";
import { useMyPerms } from "@/hooks/useMyPerms";
import { Perm } from "@/lib/perms";

export const Route = createFileRoute("/_app/me/balance")({
  component: MyBalancePage,
});

// Self-earnings only ever concern commissioned charges; the server
// applies the same restriction, so this just keeps the query key
// honest about what is being asked for.
const SELF_FILTERS: CommissionFilters = { commissioned_only: "true" };

function MyBalancePage() {
  const perms = useMyPerms();
  const redacted = perms.loaded && !perms.has(Perm.CatalogViewAll);

  const earningsQ = useQuery({
    queryKey: commissionKeys.myEarnings(),
    queryFn: fetchMyEarnings,
    refetchInterval: 30_000,
  });

  // The account itself (SM-373). Shares payoutKeys.all with the withdraw
  // panel below on purpose: submitting or cancelling a payout already
  // invalidates that key, and those are exactly the two actions that
  // move this number — so the balance and the request that changed it
  // can never be shown disagreeing.
  const balanceQ = useQuery({
    queryKey: payoutKeys.myBalance(),
    queryFn: fetchMyBalance,
    refetchInterval: 30_000,
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl tracking-tight">My balance</h1>
        <p className="max-w-[75ch] text-sm text-muted">
          Commission you have earned on polls where you are the recipient.
          Commission is a share of the fee the platform already collected —
          it is never an extra charge to a trader, and the rate was locked
          when each market first published.
        </p>
      </div>

      {/* SM-383. The withdraw panel used to sit at the BOTTOM of the
          page, under an activity table that grows without bound — so the
          one action this page exists to offer was the last thing on it,
          and on a busy account it was below several screens of history.
          It now shares the top row with the balance it spends: the
          number and the thing you do with it, side by side.

          Two columns on desktop, stacked on anything narrower, with the
          balance first in DOM order so the stacked order is
          balance → withdraw → history rather than the reverse. */}
      <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,30rem)]">
        <div className="space-y-6">
          <CurrentBalance q={balanceQ} />
          {earningsQ.isError ? (
            <LoadFailed
              what="your earnings"
              error={earningsQ.error}
              onRetry={() => void earningsQ.refetch()}
            />
          ) : (
            <div className="space-y-2">
              {/* Named as a group so the three lifetime figures below
                  read as history rather than as three more balances.
                  Without the heading, "Paid to you" sits beside "Current
                  balance" as an equal claim about what an admin has. */}
              <h2 className="text-2xs font-medium uppercase tracking-wide text-muted">
                Commission earned
              </h2>
              <div className="grid gap-4 sm:grid-cols-3">
                <BalanceTile
                  label="Paid to you"
                  hint="posted to date, including what you have withdrawn"
                  loading={earningsQ.isLoading}
                >
                  <SparkAmount value={earningsQ.data?.commission_committed} />
                </BalanceTile>
                {/* Pending is shown as its own tile rather than folded
                    into the headline: it has NOT reached the admin's
                    account, and a number that implies otherwise is the
                    one mistake this page must not make. */}
                <BalanceTile
                  label="In flight"
                  hint={`${earningsQ.data?.pending_count ?? 0} awaiting posting`}
                  loading={earningsQ.isLoading}
                >
                  <SparkAmount
                    value={earningsQ.data?.commission_pending}
                    tone="warning"
                  />
                </BalanceTile>
                <BalanceTile
                  label="Failed"
                  hint={
                    (earningsQ.data?.failed_count ?? 0) > 0
                      ? "an operator is looking into it"
                      : "none"
                  }
                  loading={earningsQ.isLoading}
                >
                  <SparkAmount
                    value={earningsQ.data?.commission_failed}
                    tone={
                      (earningsQ.data?.failed_count ?? 0) > 0
                        ? "danger"
                        : "muted"
                    }
                  />
                </BalanceTile>
              </div>
            </div>
          )}
        </div>

        {/* SM-308. The full path is live: the request holds the amount
            in escrow, another admin reviews it, and the crypto rail
            sends it on-chain — rejecting, cancelling or a failed send
            all return the money. */}
        <WithdrawPanel />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Where it came from</CardTitle>
          {redacted ? (
            <span className="text-2xs text-muted">
              Named for reconciliation only — you cannot open these polls
            </span>
          ) : null}
        </CardHeader>
        <CardBody className="p-0">
          <CommissionGroupTable
            view="event"
            filters={SELF_FILTERS}
            redacted={redacted}
            self
          />
        </CardBody>
      </Card>
    </div>
  );
}

// The account itself — what an admin may withdraw right now (SM-373).
//
// It leads the page and carries the only accent on it, because the three
// commission tiles below are LIFETIME figures: "Paid to you" still
// counts every payout that has already left, so the two numbers diverge
// permanently after the first withdrawal and the page has to say which
// is which. Nothing here is derived from those tiles — see the query's
// comment for why the arithmetic is the ledger's and not this page's.
//
// The three states AC 6 is about are kept apart deliberately: loading is
// a skeleton (no number), an unreachable ledger is an error card (no
// number), and only a real answer renders a figure. A zero balance is a
// real answer and says so plainly.
function CurrentBalance({
  q,
}: {
  q: UseQueryResult<MyBalance, unknown>;
}) {
  if (q.isLoading) {
    return (
      <section aria-label="Current balance">
        <Skeleton className="h-[104px]" />
      </section>
    );
  }
  if (q.isError) {
    return (
      <section aria-label="Current balance">
        <LoadFailed
          what="your current balance"
          error={q.error}
          onRetry={() => void q.refetch()}
        />
      </section>
    );
  }

  const held = Number(q.data?.pending_withdrawal ?? 0) > 0;
  const reserved = Number(q.data?.reserved ?? 0) > 0;
  // ACTIVE is the ordinary case and needs no badge; anything else means
  // the balance is real but cannot currently be spent, which an admin
  // would otherwise discover only when a request was refused.
  const status = q.data?.status;
  const spendable = !status || status === "ACTIVE";

  return (
    <section
      aria-label="Current balance"
      className="rounded-lg border border-accent/40 bg-accent/5 px-5 py-4"
    >
      <div className="flex flex-wrap items-start justify-between gap-x-8 gap-y-3">
        <div>
          <div className="text-2xs font-medium uppercase tracking-wide text-muted">
            Current balance
          </div>
          <div className="mt-1 font-display text-3xl tracking-tight">
            <SparkAmount value={q.data?.available} />
          </div>
          <div className="mt-1 text-2xs text-muted">
            {q.data?.provisioned === false
              ? "no commission account yet — nothing has been earned"
              : "available to withdraw now"}
          </div>
        </div>

        {/* Where the rest of the money is. Shown only when there IS a
            rest: a permanent row of zeros beside the balance reads as
            more balances. */}
        {held || reserved || !spendable ? (
          <div className="flex flex-wrap items-start gap-x-8 gap-y-3">
            {held ? (
              <Aside label="Held for withdrawal" hint="awaiting review — returned if refused">
                <SparkAmount value={q.data?.pending_withdrawal} tone="warning" />
              </Aside>
            ) : null}
            {reserved ? (
              <Aside label="Reserved" hint="held by the ledger">
                <SparkAmount value={q.data?.reserved} tone="warning" />
              </Aside>
            ) : null}
            {!spendable ? (
              <Aside label="Account" hint="this balance cannot be withdrawn">
                <span className="text-danger">{status}</span>
              </Aside>
            ) : null}
          </div>
        ) : null}
      </div>
    </section>
  );
}

function Aside({
  label,
  hint,
  children,
}: {
  label: string;
  hint: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="text-2xs font-medium uppercase tracking-wide text-muted">
        {label}
      </div>
      <div className="mt-1 text-lg tabular-nums">{children}</div>
      <div className="mt-0.5 text-2xs text-muted">{hint}</div>
    </div>
  );
}

function BalanceTile({
  label,
  hint,
  loading,
  children,
}: {
  label: string;
  hint?: string;
  loading?: boolean;
  children: React.ReactNode;
}) {
  if (loading) return <Skeleton className="h-[94px]" />;
  return (
    <div className="rounded-lg border border-border/60 bg-surface-2/40 px-4 py-3">
      <div className="text-2xs font-medium uppercase tracking-wide text-muted">
        {label}
      </div>
      <div className="mt-1 text-xl tabular-nums">{children}</div>
      {hint ? <div className="mt-0.5 text-2xs text-muted">{hint}</div> : null}
    </div>
  );
}
